import React from 'react'

import { OrderTotal } from '@pb/components'
import { formatCurrency, getLabelFromStorage, isAppDevice } from '@pb/utils'
import { path } from 'ramda'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'

import { EXTERNAL_ROUTES } from 'App/constants'
import appSelectors from 'App/duck/selectors'
import OPTIMIZELY from 'constants/optimizely'
import selectors from 'features/orderTotals/duck/selectors'
import { trackOptimizelyEvent } from 'helpers/optimizely'
import triggerExternalModal, { join } from 'helpers/triggerExternalModal'

import {
  OrderTotalData,
  OrderTotalsProps,
  OrderTotalTableData,
  OrderTotalTotalData,
  OrderTotalMemberData,
} from '../types'
import { shouldShowMaxTaxOrTBD, shouldNotShowTotalTax, getValueForLabel } from './helpers'

const OrderTotals: React.FC<OrderTotalsProps> = React.memo(
  ({
    isLoading,
    isMemberPriceFooterEnabled,
    voucherPushMessage,
    deliveryAddress,
    orderTotalCopy,
    offerDisclaimerText,
    isFreeShippingEnabled,
    isBoldText,
  }) => {
    const orderTotals = useSelector(selectors.getOrderTotals)

    const isDecoupledCheckoutEnabled = useSelector(appSelectors.getIsDecoupledCheckoutEnabled)
    const isAbTestVoucherModalEnabled = useSelector(appSelectors.getIsAbTestVoucherModalEnabled)

    const history = useHistory()
    // It should show tax information only for Checkout page
    const shouldShowTax = history.location.pathname.includes('checkout')
    const isExternalTaxApplied = useSelector(appSelectors.getIsExternalTaxApplied)

    const isDecoupledVoucherModalEnabled =
      isDecoupledCheckoutEnabled ||
      isAbTestVoucherModalEnabled ||
      process.env.IS_ORDER_TOTAL_DECOUPLED_ENABLED === 'enabled'

    // Formatted Api should not be used for serbian currency (RSD)
    const isFormattedApiEnabled = process.env.IS_FORMATTERS_API_ENABLED === 'enabled'
    const shouldFormattedApiBeUsed =
      isFormattedApiEnabled && !orderTotals?.totalPrice?.formattedValue.includes('RSD')

    if (!orderTotals) {
      return null
    }
    const shouldNotShowMaxTaxOrTBD = !shouldShowMaxTaxOrTBD(orderTotals, isExternalTaxApplied)
    const maxTaxEstValue = isExternalTaxApplied
      ? orderTotals.totalTax || {
          value: 0,
          formattedValue: getLabelFromStorage('sidecart.taxes.to.be.defined.TBD'),
        }
      : orderTotals.totalTax

    let totalData = [
      [getLabelFromStorage('basket.page.order.value'), orderTotals.whitePriceTotal],
      [getLabelFromStorage('order.totals.subtotal'), orderTotals.basePriceTotal],
      [getLabelFromStorage('basket.page.discount'), orderTotals.coloredPriceDiscount],
      [getLabelFromStorage('basket.page.promotion'), orderTotals.generalPromotionDiscount],
      [getLabelFromStorage('checkout.ordertotal.club.voucher'), orderTotals.clubVoucherDiscount],
      [getLabelFromStorage('basket.page.staffCardDiscount'), orderTotals.staffCardDiscount],
      [getLabelFromStorage('checkout.giftcard.heading'), orderTotals.totalGiftCardAmount],
      [getLabelFromStorage('basket.page.delivery.standard'), orderTotals.deliveryCost],
      [getLabelFromStorage('basket.page.payment.cost'), orderTotals.paymentCost],
      [getLabelFromStorage('order.confirmation.total'), orderTotals.totalBeforeRounding],
      [getLabelFromStorage('basket.page.rounding.amount'), orderTotals.rounding],
      [getLabelFromStorage('checkout.ordertotal.tax'), orderTotals.totalTax],
      [getLabelFromStorage('checkout.orderTotal.maxTax.estimated'), maxTaxEstValue],
    ].filter((priceRow) => {
      const priceValue = path(['value'])(priceRow[1])
      const priceLabel = priceRow[0]

      if (priceLabel === getLabelFromStorage('basket.page.delivery.standard') && priceValue === 0) {
        return true
      }

      if (
        priceLabel === getLabelFromStorage('checkout.orderTotal.maxTax.estimated') &&
        priceValue === 0 &&
        !shouldNotShowMaxTaxOrTBD
      ) {
        return true
      }
      if (
        priceLabel === getLabelFromStorage('checkout.ordertotal.tax') &&
        shouldNotShowTotalTax(orderTotals, shouldShowTax)
      ) {
        return false
      }

      if (
        priceLabel === getLabelFromStorage('checkout.orderTotal.maxTax.estimated') &&
        shouldNotShowMaxTaxOrTBD
      ) {
        return false
      }

      return !!priceValue
    })

    if (orderTotals.impositionType && shouldNotShowMaxTaxOrTBD) {
      const taxData = orderTotals.impositionType.map((type) => [
        type.description,
        type.impositionValue,
      ])
      totalData = totalData.concat(taxData)
    }

    const voucherLabels = [
      getLabelFromStorage('basket.page.discount'),
      getLabelFromStorage('basket.page.staffCardDiscount'),
      getLabelFromStorage('checkout.ordertotal.club.voucher'),
      getLabelFromStorage('basket.page.promotion'),
      getLabelFromStorage('checkout.giftcard.heading'),
    ]

    const handleBecomeMemberClick = (): void => {
      sessionStorage.setItem('become_member_cta_clicked', 'true')
      trackOptimizelyEvent(OPTIMIZELY.CLICK_ORDER_TOTALS_SIGNUP_LINK)
      if (isAppDevice()) {
        window.location.href = EXTERNAL_ROUTES.registerPage
      } else {
        triggerExternalModal(join)
      }
    }

    const orderTotalTableData = (): OrderTotalTableData[] => {
      return (totalData as OrderTotalData).map(([labelKey, { value: amount, formattedValue }]) => ({
        isDiscount: isDecoupledVoucherModalEnabled && voucherLabels.includes(labelKey),
        name: labelKey,
        value: getValueForLabel(labelKey, amount, formattedValue),
      }))
    }

    const orderTotalTotalData = (): OrderTotalTotalData | undefined => {
      return (
        orderTotals.totalPrice && {
          name: getLabelFromStorage('order.total'),
          value: `${
            shouldFormattedApiBeUsed
              ? formatCurrency(orderTotals.totalPrice.value)
              : orderTotals.totalPrice.formattedValue
          } ${orderTotals.externalTaxApplied && deliveryAddress ? ' *' : ''}`,
        }
      )
    }
    const orderTotalMemberPriceData = (): OrderTotalMemberData | undefined => {
      // Mocked 10% welcome offer discount. For full version, this value needs to be fetched
      const welcomeOfferDiscount = 0.1
      const memberValue =
        ((orderTotals.subTotal?.value || 0) -
          (orderTotals.totalYellowPrice?.value || 0) -
          ((orderTotals.totalDiscount?.value && Math.abs(orderTotals.totalDiscount.value)) || 0)) *
          (1 - welcomeOfferDiscount) +
        ((!isFreeShippingEnabled && orderTotals.deliveryCost?.value) || 0)
      return memberValue > 0
        ? {
            name: getLabelFromStorage(orderTotalCopy || ''),
            value: `${formatCurrency(memberValue)} ${
              orderTotals.externalTaxApplied && deliveryAddress ? ' *' : ''
            }`,
            linkLabel: getLabelFromStorage(`{0}${offerDisclaimerText}{1}`),
          }
        : undefined
    }

    return (
      <OrderTotal
        tableData={orderTotalTableData()}
        totalData={orderTotalTotalData()}
        memberData={orderTotalMemberPriceData()}
        onBecomeMemberLinkClick={handleBecomeMemberClick}
        isDecoupled={isDecoupledVoucherModalEnabled}
        isMemberPriceFooterEnabled={isMemberPriceFooterEnabled && !!orderTotals.totalPrice?.value}
        voucherPushMessage={voucherPushMessage}
        isLoading={isLoading}
        isBoldText={isBoldText}
      />
    )
  },
)

export default OrderTotals
